import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid, CalendarDays, Users, Ticket, ScanLine, Image as ImageIcon,
  Instagram, Send, LogOut, Trash2, Plus, Loader2, RefreshCw,
} from "lucide-react";
import api, { API, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Scanner from "@/components/site/Scanner";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "tickets", label: "Tickets", icon: Ticket },
  { id: "scanner", label: "Door Scanner", icon: ScanLine },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "blast", label: "Email Blast", icon: Send },
];

const inp = "w-full bg-brand-ink/[0.03] border border-brand-ink/10 px-3 py-2.5 text-sm outline-none focus:border-brand-orange transition-colors";
const btn = "rounded-full bg-brand-orange text-white px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] hover:bg-brand-orangeHover transition-colors flex items-center gap-2 disabled:opacity-60";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-brand-cream grain flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 bg-brand-ink text-white md:min-h-screen p-6 md:sticky md:top-0 md:h-screen flex md:flex-col gap-6 overflow-x-auto">
        <div className="hidden md:block">
          <div className="font-head font-extrabold text-2xl uppercase leading-none">MARKIE<span className="text-brand-orange">2</span>FRESH</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1">Promoter Console</div>
        </div>
        <nav className="flex md:flex-col gap-1 flex-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-3 px-4 py-3 text-left font-mono text-xs uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${
                tab === t.id ? "bg-brand-orange text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon size={16} /> <span className="hidden md:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-widest text-white/50 hover:text-brand-orange transition-colors">
          <LogOut size={16} /> <span className="hidden md:inline">Sign out</span>
        </button>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-12 max-w-6xl">
        {tab === "overview" && <Overview />}
        {tab === "events" && <Events />}
        {tab === "subscribers" && <Subscribers />}
        {tab === "tickets" && <Tickets />}
        {tab === "scanner" && <ScannerTab />}
        {tab === "gallery" && <GalleryAdmin />}
        {tab === "instagram" && <InstagramAdmin />}
        {tab === "blast" && <Blast />}
      </main>
    </div>
  );
}

function Header({ title, sub, right }) {
  return (
    <div className="flex items-end justify-between mb-8">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-orange mb-2">/ {sub}</div>
        <h1 className="font-head font-extrabold text-4xl md:text-5xl uppercase leading-none">{title}</h1>
      </div>
      {right}
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState({ subs: 0, tickets: 0, events: 0, checked: 0 });
  useEffect(() => {
    (async () => {
      const [s, t, e] = await Promise.all([
        api.get("/subscribers"),
        api.get("/tickets/admin/all"),
        api.get("/events?all=true"),
      ]);
      setStats({
        subs: s.data.count,
        tickets: t.data.length,
        events: e.data.length,
        checked: t.data.filter((x) => x.status === "used").length,
      });
    })().catch(() => {});
  }, []);
  const cards = [
    { l: "Subscribers", v: stats.subs },
    { l: "Tickets issued", v: stats.tickets },
    { l: "Checked in", v: stats.checked },
    { l: "Live events", v: stats.events },
  ];
  return (
    <div>
      <Header title="Overview" sub="Dashboard" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.l} className="bg-white border border-brand-ink/10 p-6">
            <div className="font-head font-extrabold text-6xl text-brand-orange leading-none">{c.v}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brand-ink/50 mt-3">{c.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY = { title: "", tagline: "", date: "", time: "22:00", venue: "", city: "", cover_image_url: "", price: 0, is_free: false, capacity: 200, description: "", lineup: "", is_published: true };

function Events() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/events?all=true").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, capacity: parseInt(form.capacity) || 200,
      lineup: typeof form.lineup === "string" ? form.lineup.split(",").map((s) => s.trim()).filter(Boolean) : form.lineup };
    try {
      if (editId) await api.put(`/events/${editId}`, payload);
      else await api.post("/events", payload);
      toast.success(editId ? "Event updated" : "Event created");
      setForm(EMPTY); setEditId(null); load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const edit = (ev) => { setEditId(ev.id); setForm({ ...ev, lineup: (ev.lineup || []).join(", ") }); window.scrollTo(0, 0); };
  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <Header title="Events" sub="Manage lineup" />
      <form onSubmit={save} className="bg-white border border-brand-ink/10 p-6 grid md:grid-cols-2 gap-4 mb-10" data-testid="event-form">
        <input className={inp} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        <input className={inp} placeholder="Tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
        <input className={inp} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        <input className={inp} placeholder="Time (22:00)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        <input className={inp} placeholder="Venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} required />
        <input className={inp} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
        <input className={`${inp} md:col-span-2`} placeholder="Cover image URL" value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} required />
        <input className={inp} type="number" step="0.01" placeholder="Price (USD)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
        <input className={inp} type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
        <input className={`${inp} md:col-span-2`} placeholder="Lineup (comma separated)" value={form.lineup} onChange={(e) => setForm({ ...form, lineup: e.target.value })} />
        <textarea className={`${inp} md:col-span-2`} rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-brand-ink/60">
          <input type="checkbox" checked={form.is_free} onChange={(e) => setForm({ ...form, is_free: e.target.checked })} /> Free event (RSVP only)
        </label>
        <div className="flex gap-3 md:justify-end">
          {editId && <button type="button" onClick={() => { setForm(EMPTY); setEditId(null); }} className="font-mono text-xs uppercase tracking-widest text-brand-ink/50">Cancel</button>}
          <button type="submit" disabled={saving} className={btn} data-testid="event-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} {editId ? "Update" : "Create"}
          </button>
        </div>
      </form>

      <div className="space-y-3">
        {events.map((ev) => (
          <div key={ev.id} className="bg-white border border-brand-ink/10 p-4 flex items-center gap-4" data-testid={`admin-event-${ev.id}`}>
            <img src={ev.cover_image_url} alt="" className="w-16 h-16 object-cover" />
            <div className="flex-1">
              <div className="font-head font-bold uppercase">{ev.title}</div>
              <div className="font-mono text-[11px] text-brand-ink/50">{ev.date} · {ev.city} · {ev.is_free ? "Free" : `$${ev.price}`} · Sold {ev.tickets_sold}/{ev.capacity}</div>
            </div>
            <button onClick={() => edit(ev)} className="font-mono text-xs uppercase tracking-widest text-brand-orange">Edit</button>
            <button onClick={() => del(ev.id)} className="text-brand-ink/40 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Subscribers() {
  const [data, setData] = useState({ count: 0, subscribers: [] });
  const load = () => api.get("/subscribers").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/subscribers/${id}`); load(); };
  return (
    <div>
      <Header title="Subscribers" sub={`${data.count} on the list`} />
      <div className="bg-white border border-brand-ink/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-ink/10 font-mono text-[10px] uppercase tracking-widest text-brand-ink/40">
              <th className="text-left p-4">Email</th><th className="text-left p-4">Name</th>
              <th className="text-left p-4">DOB</th><th className="text-left p-4">Source</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="subscribers-table">
            {data.subscribers.map((s) => (
              <tr key={s.id} className="border-b border-brand-ink/5">
                <td className="p-4 font-medium">{s.email}</td>
                <td className="p-4 text-brand-ink/60">{s.name || "—"}</td>
                <td className="p-4 text-brand-ink/60 font-mono text-xs">{s.dob}</td>
                <td className="p-4 text-brand-ink/40 font-mono text-[11px]">{s.source}</td>
                <td className="p-4 text-right"><button onClick={() => del(s.id)} className="text-brand-ink/30 hover:text-red-500"><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.count === 0 && <p className="p-6 text-brand-ink/40 font-mono text-sm">No subscribers yet.</p>}
      </div>
    </div>
  );
}

function Tickets() {
  const [tickets, setTickets] = useState([]);
  useEffect(() => { api.get("/tickets/admin/all").then((r) => setTickets(r.data)); }, []);
  return (
    <div>
      <Header title="Tickets" sub={`${tickets.length} issued`} />
      <div className="bg-white border border-brand-ink/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-ink/10 font-mono text-[10px] uppercase tracking-widest text-brand-ink/40">
              <th className="text-left p-4">Event</th><th className="text-left p-4">Guest</th>
              <th className="text-left p-4">Email</th><th className="text-left p-4">Type</th><th className="text-left p-4">Status</th>
            </tr>
          </thead>
          <tbody data-testid="tickets-table">
            {tickets.map((t) => (
              <tr key={t.id} className="border-b border-brand-ink/5">
                <td className="p-4 font-medium">{t.event_title}</td>
                <td className="p-4 text-brand-ink/60">{t.buyer_name}</td>
                <td className="p-4 text-brand-ink/60">{t.buyer_email}</td>
                <td className="p-4 font-mono text-[11px] uppercase text-brand-ink/40">{t.source}</td>
                <td className="p-4">
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${t.status === "used" ? "bg-brand-ink/5 text-brand-ink/50" : "bg-brand-orange/10 text-brand-orange"}`}>
                    {t.status === "used" ? "Checked in" : "Valid"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tickets.length === 0 && <p className="p-6 text-brand-ink/40 font-mono text-sm">No tickets yet.</p>}
      </div>
    </div>
  );
}

function ScannerTab() {
  return (
    <div>
      <Header title="Door Scanner" sub="Check guests in" />
      <Scanner />
    </div>
  );
}

function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [type, setType] = useState("image");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const addUrl = async (e) => {
    e.preventDefault();
    if (!url) return;
    await api.post("/gallery/url", { url, type, caption });
    setUrl(""); setCaption(""); toast.success("Added"); load();
  };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    try {
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); setCaption(""); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const del = async (id) => { await api.delete(`/gallery/${id}`); load(); };

  return (
    <div>
      <Header title="Gallery" sub="Photos & videos" />
      <div className="bg-white border border-brand-ink/10 p-6 mb-8 space-y-4">
        <form onSubmit={addUrl} className="grid md:grid-cols-[1fr_auto_auto] gap-3">
          <input className={inp} placeholder="Image / video URL" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="gallery-url-input" />
          <select className={inp} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="image">Image</option><option value="video">Video</option>
          </select>
          <button className={btn} data-testid="gallery-add-url"><Plus size={14} /> Add URL</button>
        </form>
        <input className={inp} placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} />
        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-brand-orange cursor-pointer">
          {uploading ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Upload a file
          <input type="file" className="hidden" accept="image/*,video/*" onChange={upload} data-testid="gallery-upload-input" />
        </label>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.id} className="relative group aspect-square bg-brand-ink overflow-hidden">
            {it.type === "video"
              ? <video src={it.storage_path ? `${API}/gallery/file/${it.storage_path}` : it.url} className="w-full h-full object-cover" />
              : <img src={it.storage_path ? `${API}/gallery/file/${it.storage_path}` : it.url} alt="" className="w-full h-full object-cover" />}
            <button onClick={() => del(it.id)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} className="text-red-500" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function InstagramAdmin() {
  const [status, setStatus] = useState({ connected: false });
  const [token, setToken] = useState("");
  const [posts, setPosts] = useState([]);
  const [manual, setManual] = useState({ image_url: "", caption: "" });

  const load = () => {
    api.get("/instagram/status").then((r) => setStatus(r.data));
    api.get("/instagram/feed?limit=30").then((r) => setPosts(r.data.posts || []));
  };
  useEffect(() => { load(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    try {
      await api.post("/instagram/connect", { short_lived_token: token });
      toast.success("Instagram connected"); setToken(""); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!manual.image_url) return;
    await api.post("/instagram/manual", manual);
    setManual({ image_url: "", caption: "" }); toast.success("Post added"); load();
  };

  return (
    <div>
      <Header title="Instagram" sub={status.connected ? `Connected · @${status.username}` : "Not connected"} />
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <form onSubmit={connect} className="bg-white border border-brand-ink/10 p-6 space-y-4">
          <div className="font-head font-bold text-xl uppercase">Connect real account</div>
          <p className="text-brand-ink/50 text-sm">Paste a short-lived Instagram token from your Meta App dashboard. We exchange it for a long-lived token server-side. (Requires INSTAGRAM_APP_SECRET set.)</p>
          <input className={inp} placeholder="Short-lived token" value={token} onChange={(e) => setToken(e.target.value)} data-testid="ig-token-input" />
          <button className={btn} data-testid="ig-connect"><Instagram size={14} /> Connect</button>
        </form>

        <form onSubmit={addManual} className="bg-white border border-brand-ink/10 p-6 space-y-4">
          <div className="font-head font-bold text-xl uppercase">Add post manually</div>
          <p className="text-brand-ink/50 text-sm">No API needed — curate the feed shown on your site.</p>
          <input className={inp} placeholder="Image URL" value={manual.image_url} onChange={(e) => setManual({ ...manual, image_url: e.target.value })} data-testid="ig-manual-url" />
          <input className={inp} placeholder="Caption" value={manual.caption} onChange={(e) => setManual({ ...manual, caption: e.target.value })} />
          <button className={btn} data-testid="ig-manual-add"><Plus size={14} /> Add post</button>
        </form>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        {posts.map((p) => (
          <div key={p.id} className="relative group aspect-square bg-brand-ink overflow-hidden">
            <img src={p.image_url} alt="" className="w-full h-full object-cover" />
            {p.source === "manual" && (
              <button onClick={async () => { await api.delete(`/instagram/manual/${p.id}`); load(); }} className="absolute top-2 right-2 w-8 h-8 bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} className="text-red-500" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Blast() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!subject || !message) { toast.error("Subject and message required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/subscribers/blast", { subject, message });
      toast.success(`Sent to ${data.sent} of ${data.total} subscribers`);
      setSubject(""); setMessage("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div>
      <Header title="Email Blast" sub="Reach your list" />
      <form onSubmit={send} className="bg-white border border-brand-ink/10 p-6 space-y-5 max-w-2xl" data-testid="blast-form">
        <input className={inp} placeholder="Subject line" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="blast-subject" />
        <textarea className={inp} rows={8} placeholder="Write your message…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="blast-message" />
        <button type="submit" disabled={sending} className={btn} data-testid="blast-send">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send blast
        </button>
      </form>
    </div>
  );
}
